<template>
  <div>
    <main-topbar />

    <d-container fluid class="px-0">
      <div class="main-content-wrapper">
        <!-- Main Sidebar -->
        <main-sidebar :items="getFilteredSidebarItems" />

        <main class="main-content">
          <div id="scroll-detector"></div>
          <!-- Content -->
          <slot />
        </main>
      </div>
    </d-container>
  </div>
</template>

<script>
import getSidebarItems from '@/data/sidebar-nav-items';
import MainSidebar from '@/components/layout/MainSidebar/MainSidebar.vue';
import MainTopbar from '@/components/layout/MainTopbar/MainTopbar.vue';

export default {
  name: 'main-view',
  components: {
    MainSidebar,
    MainTopbar
  },
  computed: {
    getFilteredSidebarItems() {
      const allItems = getSidebarItems();

      const filterRecursive = (items) => items.reduce((acc, item) => {
        const hasPermission = item.permission === undefined
          || item.permission === false
          || this.$store.getters.getUserPermission(item.permission);

        if (!hasPermission) {
          return acc;
        }

        const newItem = { ...item };

        if (newItem.items && newItem.items.length > 0) {
          newItem.items = filterRecursive(newItem.items);
        }

        if (!newItem.to && (!newItem.items || newItem.items.length === 0)) {
          return acc;
        }

        acc.push(newItem);
        return acc;
      }, []);

      return filterRecursive(allItems);
    }
  },
  data () {
    return {
      scrollObserver: null,
      scrollDetector: null,
      headerResizeObserver: null,
      currentHeaderEl: null,
      cachedTopbarEl: null,
      domObserver: null,
      checkHeaderTimeout: null
    };
  },
  watch: {
    $route() {
      this.debouncedCheckForPageHeader();
    }
  },
  mounted () {
    if ('IntersectionObserver' in window) {
      this.scrollDetector = document.querySelector('#scroll-detector');
      this.scrollObserver = new IntersectionObserver(
        ([e]) => {
          if (e.intersectionRatio === 0) {
            document.body.classList.add('is-scrolled');
          }

          if (e.intersectionRatio === 1) {
            document.body.classList.remove('is-scrolled');
          }
        },
        {
          threshold: [0, 1]
        }
      );
      this.scrollObserver.observe(this.scrollDetector);
    }

    document.body.classList.add('js-not-close-modals-on-backdrop-click');

    this.cachedTopbarEl = document.querySelector('.top-bar.sticky-top');

    this.setupHeaderTracking();
  },
  beforeDestroy () {
    if ('IntersectionObserver' in window && this.scrollObserver) {
      this.scrollObserver.unobserve(this.scrollDetector);
    }

    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect();
    }

    if (this.domObserver) {
      this.domObserver.disconnect();
    }

    if (this.checkHeaderTimeout) {
      clearTimeout(this.checkHeaderTimeout);
    }
  },
  methods: {
    debouncedCheckForPageHeader () {
      if (this.checkHeaderTimeout) {
        clearTimeout(this.checkHeaderTimeout);
      }

      this.checkHeaderTimeout = setTimeout(() => {
        this.checkForPageHeader();
      }, 100);
    },
    setupHeaderTracking () {
      if ('ResizeObserver' in window) {
        this.headerResizeObserver = new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            this.updateHeaderCssVariable(entry.target.offsetHeight);
          });
        });
      }

      this.domObserver = new MutationObserver(() => {
        this.debouncedCheckForPageHeader();
      });

      const mainContentArea = this.$el.querySelector('.main-content');
      if (mainContentArea) {
        this.domObserver.observe(mainContentArea, { childList: true, subtree: true });
      }

      this.debouncedCheckForPageHeader();
    },
    checkForPageHeader () {
      const headerEl = this.$el.querySelector('.page-header');
      if (headerEl && headerEl !== this.currentHeaderEl) {
        if (this.currentHeaderEl && this.headerResizeObserver) {
          this.headerResizeObserver.unobserve(this.currentHeaderEl);
        }

        this.currentHeaderEl = headerEl;

        if (this.headerResizeObserver) {
          this.headerResizeObserver.observe(this.currentHeaderEl);
        }

        this.updateHeaderCssVariable(this.currentHeaderEl.offsetHeight);
      } else if (!headerEl && this.currentHeaderEl) {
        if (this.headerResizeObserver) {
          this.headerResizeObserver.unobserve(this.currentHeaderEl);
        }
        this.currentHeaderEl = null;
        this.updateHeaderCssVariable(0);
      }
    },
    updateHeaderCssVariable (height) {
      let topbarHeight = 0;

      if (this.cachedTopbarEl) {
        topbarHeight = this.cachedTopbarEl.offsetHeight - 10;
      } else {
        const dynamicTopbar = document.querySelector('.top-bar.sticky-top');
        if (dynamicTopbar) {
          this.cachedTopbarEl = dynamicTopbar;
          topbarHeight = this.cachedTopbarEl.offsetHeight - 10;
        }
      }

      document.documentElement.style.setProperty('--page-header-height', `${height + topbarHeight}px`);
    }
  }
};
</script>

<style lang="scss">
body > .container-fluid,
body > .container-fluid > .row {
  min-height: 100vh;
}

body > .container-fluid > .row > .main-content {
  position: relative;
}

// TODO: do czego to?
.modal {
  left: 220px;
  width: calc(100% - 220px);
}

#scroll-detector {
  background: transparent;
  height: 1px;
  width: 100%;
}

.main-content-wrapper {
  position: relative;
  display: flex;
}
</style>
